import { useState } from "react";
import type { SuperAdminUserDetail, SuperAdminUserSession } from "@multi-chat/shared";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "../../components/ui/badge/index.js";
import { Button } from "../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogActions,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog/index.js";
import { LtrValue } from "../../components/common/LtrValue.js";
import { ProductErrorState } from "../../components/common/ProductErrorState.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";
import type { SuperAdminLoadState } from "./SuperAdminDashboardPage.js";
import { SuperAdminUserStatusBadge } from "./SuperAdminUsersPage.js";
import {
  contactsTableHeaderLabelClass,
  superAdminTableBodyCellClass,
  superAdminTableBodyRowClass,
  superAdminTableClass,
  superAdminTableHeaderCellClass,
  superAdminTableHeadRowClass,
  superAdminTableScrollClass,
  superAdminTableWorkspaceClass,
} from "./super-admin-table-presentation.js";

export interface SuperAdminUserDetailData {
  user: SuperAdminUserDetail;
  sessions: SuperAdminUserSession[];
}

type RevokeTarget = { kind: "one"; session: SuperAdminUserSession } | { kind: "all" };

export function SuperAdminUserDetailView({
  state,
  onRetry,
  onRevokeSession,
  onRevokeAll,
}: {
  state: SuperAdminLoadState<SuperAdminUserDetailData>;
  onRetry: () => void;
  onRevokeSession: (session: SuperAdminUserSession) => Promise<void>;
  onRevokeAll: () => Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();

  if (state.status === "loading") {
    return (
      <div className="h-48 animate-pulse rounded-xl border border-line bg-surface" data-testid="super-admin-user-loading" aria-busy="true" />
    );
  }

  if (state.status === "error") {
    return (
      <ProductErrorState
        testId="super-admin-user-error"
        title={t("superAdmin.users.detail.loadErrorTitle")}
        description={t("superAdmin.users.detail.loadErrorBody")}
        onRetry={onRetry}
        retryLabel={t("superAdmin.retry")}
      />
    );
  }

  const { user, sessions } = state.data;
  return (
    <div className="grid gap-6" data-testid="super-admin-user-detail">
      <div className="grid gap-2">
        <Link to="/super-admin/users" className="text-sm text-muted no-underline hover:underline">
          {t("superAdmin.users.detail.back")}
        </Link>
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h1 className="m-0 text-xl font-semibold text-foreground">{user.displayName}</h1>
          <SuperAdminUserStatusBadge status={user.status} />
          {user.platformAdmin ? (
            <Badge variant="outline" data-testid="super-admin-user-platform-admin">
              {t("superAdmin.users.detail.platformAdmin")}
            </Badge>
          ) : null}
        </div>
        <p className="m-0 text-sm text-muted">
          <LtrValue>{user.email}</LtrValue>
          <span className="px-2">·</span>
          {t("superAdmin.detail.id")} <LtrValue>{user.id}</LtrValue>
          <span className="px-2">·</span>
          {t("superAdmin.detail.created")}{" "}
          <LtrValue>{new Date(user.createdAt).toLocaleDateString(i18n.language)}</LtrValue>
          <span className="px-2">·</span>
          <Link
            to={`/super-admin/logs?userId=${user.id}`}
            className="text-foreground no-underline hover:underline"
            data-testid="super-admin-user-view-logs"
          >
            {t("superAdmin.logs.viewLogs")}
          </Link>
        </p>
      </div>

      <section className="grid gap-3">
        <h2 className="m-0 text-sm font-semibold text-foreground">{t("superAdmin.users.detail.memberships")}</h2>
        {user.memberships.length === 0 ? (
          <p className="m-0 text-sm text-muted" data-testid="super-admin-user-memberships-empty">
            {t("superAdmin.users.detail.noMemberships")}
          </p>
        ) : (
          <div className={superAdminTableWorkspaceClass}>
            <div className={superAdminTableScrollClass}>
              <table
                className={`${superAdminTableClass} min-w-[32rem]`}
                data-testid="super-admin-user-memberships"
              >
                <thead>
                  <tr className={superAdminTableHeadRowClass}>
                    <th className={superAdminTableHeaderCellClass}>
                      <span className={contactsTableHeaderLabelClass}>
                        {t("superAdmin.organizations.columns.organization")}
                      </span>
                    </th>
                    <th className={superAdminTableHeaderCellClass}>
                      <span className={contactsTableHeaderLabelClass}>
                        {t("superAdmin.users.detail.role")}
                      </span>
                    </th>
                    <th className={superAdminTableHeaderCellClass}>
                      <span className={contactsTableHeaderLabelClass}>
                        {t("superAdmin.users.detail.memberStatus")}
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {user.memberships.map((membership) => (
                    <tr key={membership.organizationMemberId} className={superAdminTableBodyRowClass}>
                      <td className={superAdminTableBodyCellClass}>
                        <span className="flex flex-wrap items-center gap-2">
                          <Link
                            to={`/super-admin/organizations/${membership.organizationId}`}
                            className="font-medium text-foreground no-underline hover:underline"
                          >
                            {membership.organizationName}
                          </Link>
                          {membership.organizationStatus === "SUSPENDED" ? (
                            <Badge variant="warning">{t("superAdmin.status.SUSPENDED")}</Badge>
                          ) : null}
                        </span>
                      </td>
                      <td className={superAdminTableBodyCellClass}>
                        {t(`organization.role.${membership.role.toLowerCase()}`)}
                      </td>
                      <td className={superAdminTableBodyCellClass}>
                        {t(`superAdmin.users.memberStatus.${membership.memberStatus}`)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      <UserSessionsSection sessions={sessions} onRevokeSession={onRevokeSession} onRevokeAll={onRevokeAll} />
    </div>
  );
}

function UserSessionsSection({
  sessions,
  onRevokeSession,
  onRevokeAll,
}: {
  sessions: SuperAdminUserSession[];
  onRevokeSession: (session: SuperAdminUserSession) => Promise<void>;
  onRevokeAll: () => Promise<void>;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const notifications = useOptionalNotifications();
  const [target, setTarget] = useState<RevokeTarget | null>(null);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const formatTime = (value: string) =>
    new Date(value).toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" });
  const includesCurrent =
    target?.kind === "all" ? sessions.some((session) => session.current) : target?.session.current === true;

  async function confirm(): Promise<void> {
    if (!target) return;
    setPending(true);
    setFailed(false);
    try {
      if (target.kind === "all") {
        await onRevokeAll();
      } else {
        await onRevokeSession(target.session);
      }
      setTarget(null);
      notifications?.pushNotification({ message: t("superAdmin.users.sessions.revoked") });
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
    }
  }

  return (
    <section className="grid gap-3" data-testid="super-admin-user-sessions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="m-0 text-sm font-semibold text-foreground">{t("superAdmin.users.sessions.title")}</h2>
          <p className="m-0 mt-1 text-sm text-muted">{t("superAdmin.users.sessions.hint")}</p>
        </div>
        {sessions.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 border-destructive/35 bg-transparent text-destructive hover:bg-error-muted"
            data-testid="super-admin-revoke-all"
            onClick={() => {
              setFailed(false);
              setTarget({ kind: "all" });
            }}
          >
            {t("superAdmin.users.sessions.revokeAll")}
          </Button>
        ) : null}
      </div>
      {sessions.length === 0 ? (
        <p className="m-0 text-sm text-muted" data-testid="super-admin-user-sessions-empty">
          {t("superAdmin.users.sessions.empty")}
        </p>
      ) : (
        <ul className="m-0 list-none divide-y divide-line overflow-hidden rounded-xl border border-line bg-surface p-0">
          {sessions.map((session) => (
            <li
              key={session.id}
              className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
              data-testid={`super-admin-session-${session.id}`}
            >
              <div className="grid min-w-0 gap-0.5 text-[0.8125rem]">
                <span className="flex flex-wrap items-center gap-2 font-medium text-foreground">
                  {t("superAdmin.users.sessions.lastUsed")}{" "}
                  <bdi>{formatTime(session.lastUsedAt ?? session.createdAt)}</bdi>
                  {session.current ? <Badge variant="outline">{t("superAdmin.users.sessions.current")}</Badge> : null}
                </span>
                <span className="text-muted">
                  {t("superAdmin.users.sessions.signedIn")} <bdi>{formatTime(session.createdAt)}</bdi>
                  <span className="px-2">·</span>
                  {t("superAdmin.users.sessions.expires")} <bdi>{formatTime(session.expiresAt)}</bdi>
                </span>
                {session.ipAddress || session.userAgent ? (
                  <span className="max-w-xl truncate text-[0.75rem] text-subtle">
                    <LtrValue>{[session.ipAddress, session.userAgent].filter(Boolean).join(" · ")}</LtrValue>
                  </span>
                ) : null}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0"
                data-testid={`super-admin-revoke-session-${session.id}`}
                onClick={() => {
                  setFailed(false);
                  setTarget({ kind: "one", session });
                }}
              >
                {t("superAdmin.users.sessions.revoke")}
              </Button>
            </li>
          ))}
        </ul>
      )}
      <AlertDialog
        open={target !== null}
        onOpenChange={(open) => {
          if (pending) return;
          if (!open) {
            setTarget(null);
            setFailed(false);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogTitle>
            {target?.kind === "all"
              ? t("superAdmin.users.sessions.revokeAllDialog.title")
              : t("superAdmin.users.sessions.revokeDialog.title")}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {target?.kind === "all"
              ? t("superAdmin.users.sessions.revokeAllDialog.body")
              : t("superAdmin.users.sessions.revokeDialog.body")}
          </AlertDialogDescription>
          {includesCurrent ? (
            <p className="m-0 text-sm text-warning" data-testid="super-admin-revoke-current-warning">
              {t("superAdmin.users.sessions.currentWarning")}
            </p>
          ) : null}
          {failed ? (
            <p className="m-0 text-sm text-destructive" role="alert">
              {t("superAdmin.users.sessions.revokeError")}
            </p>
          ) : null}
          <AlertDialogActions>
            <Button type="button" variant="outline" size="sm" disabled={pending} onClick={() => setTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              disabled={pending}
              data-testid="super-admin-revoke-confirm"
              onClick={() => void confirm()}
            >
              {target?.kind === "all"
                ? t("superAdmin.users.sessions.revokeAll")
                : t("superAdmin.users.sessions.revoke")}
            </Button>
          </AlertDialogActions>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
